import { useState } from "react";
import { Mail, Phone, MapPin, Clock, ArrowRight, CheckCircle2, MessageCircle } from "lucide-react";
import { z } from "zod";
import { SectionHeader } from "./Section";
/* import emailjs from "@emailjs/browser"; */

const schema = z.object({
  name: z.string().trim().min(2, "Enter your name").max(100),
  company: z.string().trim().min(2, "Enter your company").max(120),
  email: z.string().trim().email("Enter a valid email").max(200),
  phone: z.string().trim().min(5, "Enter a valid phone").max(40),
  service: z.string().min(1, "Select a service"),
  message: z.string().trim().min(10, "Tell us a bit more").max(2000),
});

const SERVICES = [
  "Crane Inspection & Maintenance",
  "Lifting Equipment Inspection",
  "Lifting Gear Inspection",
  "Offshore Inspection Solutions",
  "Earth Moving Equipment Inspection",
  "Non Destructive Testing (NDT)",
  "Rope Access Inspection",
  "DROPS Inspection Services",
  "Load Testing Services",
  "API Category III & IV Inspections",
  "Handling Tools Inspection",
  "Derrick, Mast & Substructure Inspection",
  "Offshore Marine Services",
  "Overhauling & Refurbishment",
  "CCU Inspection & Fabrication",
  "Certified Spare Parts Supply",
  "Training Programs",
  "Other",
];

export function Contact() {
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState(false);




  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const fd = new FormData(e.currentTarget);
    const data = Object.fromEntries(fd.entries());

    const r = schema.safeParse(data);

    if (!r.success) {
      const errs: Record<string, string> = {};

      r.error.issues.forEach((i) => {
        errs[String(i.path[0])] = i.message;
      });

      setErrors(errs);
      return;
    }

    setErrors({});

    const subject = encodeURIComponent(
      `Consultation Request - ${r.data.service}`
    );

    const body = encodeURIComponent(`
Name: ${r.data.name}

Company: ${r.data.company}

Email: ${r.data.email}

Phone: ${r.data.phone}

Service: ${r.data.service}

Message:
${r.data.message}
`);

    window.location.href =
      `mailto:info@solidproofenergy.com?subject=${subject}&body=${body}`;

    setSubmitted(true);
  };

  return (
    <section id="contact" className="section-y bg-background">
      <div className="container-x">
        <SectionHeader
          eyebrow="Contact Us"
          title="Request a Service."
          intro="Tell us about your scope — onshore or offshore, single asset or full fleet — and our technical team will respond within one business day."
          align="center"
        />

        <div className="mt-14 grid gap-10 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <div className="rounded-2xl bg-navy-deep p-8 text-white">
              <h3 className="text-xl font-bold">Company Information</h3>
              <p className="mt-2 text-sm text-white/70">
                Reach out via any channel below — our operations desk is staffed 24/7 for urgent
                offshore mobilizations.
              </p>

              <div className="mt-8 space-y-5">
                {[
                  {
                    icon: MapPin,
                    label: "Head Office",
                    value:
                      "Floor 03, Aljana Tower, 07 Omar Ibn Abdel Aziz ST, Manshiet Gamal Abdel Naser, Helwan, Cairo, Egypt",
                  },
                  {
                    icon: Phone,
                    label: "Phone",
                    value: "+20 109 646 6561",
                    href: "tel:+201096466561",
                  },
                  {
                    icon: Phone,
                    label: "Phone",
                    value: "+20 150 908 0807",
                    href: "tel:+201509080807",
                  },
                  {
                    icon: MessageCircle,
                    label: "whatsapp",
                    value: "+20 109 646 6561",
                    href: "https://wa.me/+201096466561",
                  },
                  {
                    icon: Mail,
                    label: "Email",
                    value: "info@solidproofenergy.com",
                    href: "mailto:info@solidproofenergy.com",
                  },
                  {
                    icon: Clock,
                    label: "Operations Desk",
                    value: "24 / 7 — Offshore & Onshore",
                  },
                ].map((c, index) => (
                  <div key={index} className="flex items-start gap-4">
                    {c.href ? (
                      <a
                        href={c.href}
                        className="grid h-11 w-11 shrink-0 place-items-center rounded-md bg-orange/15 text-orange hover:bg-orange hover:text-white transition-colors"
                      >
                        <c.icon className="h-5 w-5" />
                      </a>
                    ) : (
                      <span className="grid h-11 w-11 shrink-0 place-items-center rounded-md bg-orange/15 text-orange">
                        <c.icon className="h-5 w-5" />
                      </span>
                    )}

                    <div className="min-w-0">
                      <div className="text-xs font-bold uppercase tracking-widest text-orange">
                        {c.label}
                      </div>

                      {c.href ? (
                        <a
                          href={c.href}
                          className="mt-1 block text-sm text-white hover:text-orange transition-colors"
                        >
                          {c.value}
                        </a>
                      ) : (
                        <div className="mt-1 text-sm text-white">{c.value}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-8 overflow-hidden rounded-xl border border-white/10">
                <iframe
                  title="Solid Proof Energy Location"
                  src="https://maps.google.com/maps?q=Floor%2003%20Aljana%20Tower%2007%20Omar%20Ibn%20Abdel%20Aziz%20ST%20Helwan%20Cairo%20Egypt&t=&z=15&ie=UTF8&iwloc=&output=embed"
                  width="100%"
                  height="280"
                  style={{ border: 0 }}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>

            </div>
          </div>

          <div className="lg:col-span-7">
            {submitted ? (
              <div className="grid h-full place-items-center rounded-2xl border border-border bg-light-gray p-10 text-center">
                <div>
                  <CheckCircle2 className="mx-auto h-14 w-14 text-orange" />
                  <h3 className="mt-4 text-2xl font-bold text-navy-deep">Request received</h3>
                  <p className="mt-2 max-w-md text-muted-foreground">
                    Thank you. Our technical team will review your enquiry and respond within one
                    business day.
                  </p>
                </div>
              </div>
            ) : (
              <form
                onSubmit={onSubmit}
                className="rounded-2xl border border-border bg-card p-8 shadow-sm"
              >
                <div className="grid gap-5 sm:grid-cols-2">
                  <Field label="Full Name" name="name" error={errors.name} />
                  <Field label="Company" name="company" error={errors.company} />
                  <Field label="Email" name="email" type="email" error={errors.email} />
                  <Field label="Phone" name="phone" type="tel" error={errors.phone} />
                  <div className="sm:col-span-2">
                    <label className="text-xs font-bold uppercase tracking-widest text-navy-deep">
                      Service Required
                    </label>
                    <select
                      name="service"
                      defaultValue=""
                      className="mt-2 w-full rounded-md border border-input bg-background px-4 py-3 text-sm text-navy-deep outline-none focus:border-orange focus:ring-2 focus:ring-orange/20"
                    >
                      <option value="" disabled>
                        Select a service…
                      </option>
                      {SERVICES.map((s) => (
                        <option key={s}>{s}</option>
                      ))}
                    </select>
                    {errors.service && (
                      <p className="mt-1 text-xs text-destructive">{errors.service}</p>
                    )}
                  </div>
                  <div className="sm:col-span-2">
                    <label className="text-xs font-bold uppercase tracking-widest text-navy-deep">
                      Message
                    </label>
                    <textarea
                      name="message"
                      rows={5}
                      placeholder="Describe your scope, asset type, location and timing…"
                      className="mt-2 w-full rounded-md border border-input bg-background px-4 py-3 text-sm text-navy-deep outline-none focus:border-orange focus:ring-2 focus:ring-orange/20"
                    />
                    {errors.message && (
                      <p className="mt-1 text-xs text-destructive">{errors.message}</p>
                    )}
                  </div>
                </div>
                <button
                  type="submit"
                  className="mt-7 inline-flex w-full items-center justify-center gap-2 rounded-md bg-orange px-6 py-4 text-base font-semibold text-white shadow-lg transition-all hover:bg-orange-hover sm:w-auto"
                >
                  Request Service <ArrowRight className="h-5 w-5" />
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({
  label,
  name,
  type = "text",
  error,
}: {
  label: string;
  name: string;
  type?: string;
  error?: string;
}) {
  return (
    <div>
      <label className="text-xs font-bold uppercase tracking-widest text-navy-deep">{label}</label>
      <input
        name={name}
        type={type}
        className="mt-2 w-full rounded-md border border-input bg-background px-4 py-3 text-sm text-navy-deep outline-none focus:border-orange focus:ring-2 focus:ring-orange/20"
      />
      {error && <p className="mt-1 text-xs text-destructive">{error}</p>}
    </div>
  );
}
